import type {ReactNode} from 'react';
import {cn} from '../lib/cn';

type SectionGroupItemProps = {
  className?: string;
  contentClassName?: string;
  title?: ReactNode;
  subtitle?: ReactNode;
  children?: ReactNode;
  divider?: boolean;
};

const SectionGroupItem = ({
  className,
  contentClassName,
  title,
  subtitle,
  children,
  divider = true,
}: SectionGroupItemProps) => {
  return (
    <section className={cn('flex flex-col gap-5', className)}>
      {divider && (
        <div className={cn('h-px w-full bg-[var(--section-group-divider-bg)]')} />
      )}
      {(title || subtitle) && (
        <div className="flex flex-col gap-1 px-8">
          {title && (
            <h3 className="text-base font-semibold leading-6 text-[var(--section-group-muted-text)]">
              {title}
            </h3>
          )}
          {subtitle && (
            <p className="text-sm font-medium leading-6 text-[var(--section-group-muted-text)]">
              {subtitle}
            </p>
          )}
        </div>
      )}
      <div className={cn('flex flex-col gap-4 px-8', contentClassName)}>{children}</div>
    </section>
  );
};

type SectionGroupProps = {
  className?: string;
  title?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
};

const SectionGroupRoot = ({className, title, actions, children}: SectionGroupProps) => {
  return (
    <div
      className={cn(
        `flex flex-col gap-6 rounded-2xl border border-[var(--section-group-border)]
        bg-[var(--section-group-bg)] py-6`,
        className,
      )}
    >
      {(title || actions) && (
        <div className="flex items-center gap-6 px-8">
          {title && (
            <h2 className="flex-1 text-lg font-bold leading-6 text-[var(--section-group-title-color)] opacity-90">
              {title}
            </h2>
          )}
          {actions && <div className="flex items-center gap-2">{actions}</div>}
        </div>
      )}
      {children}
    </div>
  );
};

type SectionGroupLabelProps = {
  id?: string;
  title: ReactNode;
  description?: ReactNode;
};

const SectionGroupLabel = ({id, title, description}: SectionGroupLabelProps) => (
  <div id={id}>
    <div className="font-semibold leading-6">{title}</div>
    {description && (
      <div className="text-sm font-medium leading-6 text-[var(--section-group-muted-text)]">
        {description}
      </div>
    )}
  </div>
);

const SectionGroup = Object.assign(SectionGroupRoot, {
  Item: SectionGroupItem,
  Label: SectionGroupLabel,
});

export {SectionGroup};
export type {SectionGroupItemProps, SectionGroupLabelProps, SectionGroupProps};
